import {
  AdminCustomProviderSelect,
  AdminDollarInput2,
  Button,
  Div,
  ErrorToast,
  H5,
  Icon,
  Modal,
  PlusIcon,
  PropsModal,
  SuccessToast,
  TrashIconRed,
  updateMenuPosModifierOption,
  reportError,
} from '@vgrubs/components';
import {
  MenuOptionPriceCustomizationV2Response,
  MenuV2Response,
  ModifierItemV2Response,
} from 'components/src/types/menu.types';
import { Provider } from 'components/src/types/shared.types';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useParams } from 'react-router';

const allProviders: Provider[] = [Provider.vGrubs, Provider.bytes];

export const EditModifierModal = ({
  editModifierModal,
  closeModal,
  setMenu,
  setLoading,
  handleChangeShouldPublish,
}: PropsModal & {
  editModifierModal: ModifierItemV2Response | null;
  setMenu: Dispatch<SetStateAction<MenuV2Response | null>>;
  handleChangeShouldPublish: (shouldPublish: boolean) => void;
}) => {
  const [price, setPrice] = useState(0);
  const [customizedPrices, setCustomizedPrices] = useState<
    MenuOptionPriceCustomizationV2Response[]
  >([]);

  const modifierItem = editModifierModal;

  const handlePriceChange = useCallback((val: number) => {
    setPrice(val);
  }, []);

  useEffect(() => {
    if (modifierItem) {
      setPrice(modifierItem.price ?? 0);
      setCustomizedPrices(modifierItem.customizedPrices ?? []);
    } else {
      setPrice(0);
      setCustomizedPrices([]);
    }
  }, [modifierItem]);

  const { locationId, brandId } = useParams() as {
    locationId: string;
    brandId: string;
  };

  const handleAddCustomizedPriceRow = () => {
    setCustomizedPrices((prev) => [
      ...prev,
      { provider: null as unknown as Provider, price: 0 },
    ]);
  };

  const handleChangeProvider = (index: number, provider: Provider | null) => {
    setCustomizedPrices((prev) =>
      prev.map((p, i) =>
        i === index ? { ...p, provider: provider as Provider } : p
      )
    );
  };

  const handleChangePrice = (index: number, value: number) => {
    setCustomizedPrices((prev) =>
      prev.map((p, i) => (i === index ? { ...p, price: value } : p))
    );
  };

  const handleRemoveCustomizedPrice = (index: number) => {
    setCustomizedPrices((prev) => prev.filter((_, i) => i !== index));
  };

  async function handleSubmit() {
    if (!modifierItem?.id) return;
    for (const cp of customizedPrices) {
      if (!cp.provider)
        return ErrorToast('Please select provider for all custom prices');
    }
    try {
      setLoading?.(true);
      const { data } = await updateMenuPosModifierOption(
        locationId,
        brandId,
        modifierItem.id,
        {
          price,
          customizedPrices,
        }
      );

      setMenu(data?.data);
      handleChangeShouldPublish(true);
      SuccessToast(data?.message);
      closeModal?.();
    } catch (error) {
      reportError(error);
    } finally {
      setLoading?.(false);
    }
  }

  return (
    <Modal
      isOpen={!!editModifierModal}
      closeModal={closeModal}
      header="Edit Modifier"
      logo={false}
      zIndex={101}
      width="600px"
      overflow="visible"
    >
      <Div $flex $column $gap="24px">
        <AdminDollarInput2
          label="Default Price"
          $textCenter
          onValueChange={handlePriceChange}
          value={price}
        />

        <Div $width="100%">
          <Div $flex $between $alignCenter $mb="12">
            <H5 $m="0">Price per Providers</H5>
            {customizedPrices.length < allProviders.length && (
              <Icon $pointer onClick={handleAddCustomizedPriceRow}>
                <PlusIcon />
              </Icon>
            )}
          </Div>

          {customizedPrices.length > 0 && (
            <Div $flex $column $gap="8px" $gapSM="12px">
              {customizedPrices.map((cp, i) => {
                const filteredProviders = allProviders.filter(
                  (p) =>
                    p === cp.provider ||
                    !customizedPrices.some((other) => other.provider === p)
                );
                return (
                  <Div $flex $alignCenter $gap="16px" $width="100%" key={i}>
                    <Div
                      $flex
                      $alignCenter
                      $gap="16px"
                      $columnSM
                      $gapSM="4px"
                      $width="100%"
                    >
                      <Div $width="100%">
                        <AdminCustomProviderSelect
                          value={cp.provider}
                          onChange={(v) =>
                            handleChangeProvider(i, v as Provider | null)
                          }
                          providerArray={filteredProviders}
                          placeholder="Select Provider"
                          emptyPlaceholder="No more providers to add"
                          $shadowDesign={false}
                        />
                      </Div>
                      <Div $width="100%">
                        <AdminDollarInput2
                          $textCenter
                          onValueChange={(v: number) => handleChangePrice(i, v)}
                          value={cp.price}
                        />
                      </Div>
                    </Div>
                    <Icon
                      $pointer
                      onClick={() => handleRemoveCustomizedPrice(i)}
                    >
                      <TrashIconRed />
                    </Icon>
                  </Div>
                );
              })}
            </Div>
          )}
        </Div>

        <Div $flex $columnSM $gapSM="16px" $gap="24px">
          <Button $selected $gray $w="100%" onClick={() => closeModal?.()}>
            Cancel
          </Button>
          <Button $selected $w="100%" onClick={handleSubmit}>
            Confirm
          </Button>
        </Div>
      </Div>
    </Modal>
  );
};
